import * as THREE from 'three';

export type FaceSide = 'auto' | 'front' | 'back' | 'left' | 'right' | 'top';

export interface DroneFace {
  /** Face center (model space), on the plane through the model center */
  center: THREE.Vector3;
  /** Outward normal of the face */
  normal: THREE.Vector3;
  /** Distance along the normal from `center` to sit just in front of the face rim */
  depth: number;
  /** Diameter of the black faceplate that holds the pixel face */
  plateDiameter: number;
  /** Small red indicator parts to hide behind the new faceplate */
  dots: THREE.Mesh[];
}

const SIDE_DIR: Record<Exclude<FaceSide, 'auto'>, THREE.Vector3> = {
  front: new THREE.Vector3(0, 0, 1),
  back: new THREE.Vector3(0, 0, -1),
  left: new THREE.Vector3(-1, 0, 0),
  right: new THREE.Vector3(1, 0, 0),
  top: new THREE.Vector3(0, 1, 0),
};

type ColorMaterial = THREE.Material & { color?: THREE.Color; emissive?: THREE.Color };

const isRedColor = (c?: THREE.Color) => !!c && c.r > 0.25 && c.r > c.g * 2 && c.r > c.b * 2;
const isRed = (m: ColorMaterial) => isRedColor(m.color) || isRedColor(m.emissive);

/**
 * Locates the drone's circular face (the dark disc with the three red dots).
 * - The side pods define the drone's width, so the face looks along the shorter horizontal axis.
 * - The red dots tell which side is front and where the face center is.
 * - Raycasts measure the face rim so the new faceplate sits just in front of it.
 */
export function locateDroneFace(
  model: THREE.Object3D,
  center: THREE.Vector3,
  size: THREE.Vector3,
  side: FaceSide,
): DroneFace {
  model.updateMatrixWorld(true);
  const maxDim = Math.max(size.x, size.y, size.z);

  // Small red parts (the indicator dots)
  const dots: THREE.Mesh[] = [];
  const box = new THREE.Box3();
  const tmp = new THREE.Vector3();
  model.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) as ColorMaterial[];
    if (!mats.some(isRed)) return;
    box.setFromObject(mesh).getSize(tmp);
    if (Math.max(tmp.x, tmp.y, tmp.z) < maxDim * 0.12) dots.push(mesh);
  });

  // Cluster the dots, ignoring strays away from the face
  let cluster = dots.map((d) => new THREE.Box3().setFromObject(d).getCenter(new THREE.Vector3()));
  if (cluster.length >= 3) {
    const mid = centroid(cluster);
    const dists = cluster.map((p) => p.distanceTo(mid)).sort((a, b) => a - b);
    const limit = dists[Math.floor(dists.length / 2)] * 1.6 + 1e-6;
    cluster = cluster.filter((p) => p.distanceTo(mid) <= limit);
  }
  const dotCenter = cluster.length > 0 ? centroid(cluster) : null;

  // Facing direction
  let normal: THREE.Vector3;
  if (side !== 'auto') {
    normal = SIDE_DIR[side].clone();
  } else {
    const axis = size.x >= size.z ? 2 : 0; // pods stick out sideways → face looks along the other axis
    let sign = 1;
    if (dotCenter) {
      const off = dotCenter.getComponent(axis) - center.getComponent(axis);
      if (Math.abs(off) > maxDim * 0.01) sign = Math.sign(off);
    }
    normal = new THREE.Vector3().setComponent(axis, sign);
  }

  // Face center: dots' centroid if they're on this side, else the model center
  const useDots = dotCenter && dotCenter.clone().sub(center).dot(normal) > 0;
  const faceCenter = (useDots ? dotCenter : center).clone();
  faceCenter.sub(normal.clone().multiplyScalar(faceCenter.clone().sub(center).dot(normal)));

  // Faceplate a bit larger than the original dark disc
  const upExtent = Math.abs(normal.y) > 0.5 ? size.z : size.y;
  const plateDiameter = upExtent * 0.56;

  // Measure the face surface + rim so the plate floats just in front
  const u = new THREE.Vector3(0, 1, 0);
  if (Math.abs(u.dot(normal)) > 0.9) u.set(0, 0, 1);
  u.projectOnPlane(normal).normalize();
  const v = new THREE.Vector3().crossVectors(normal, u);
  const reach = maxDim * 2;
  const raycaster = new THREE.Raycaster();
  const dotSet = new Set<THREE.Object3D>(dots);
  const back = normal.clone().negate();

  const depthAt = (offset: THREE.Vector3): number | null => {
    const origin = faceCenter.clone().add(offset).addScaledVector(normal, reach);
    raycaster.set(origin, back);
    raycaster.far = reach * 2;
    const hit = raycaster.intersectObject(model, true).find((h) => !dotSet.has(h.object));
    return hit ? hit.point.clone().sub(faceCenter).dot(normal) : null;
  };

  const samples: Array<number | null> = [depthAt(new THREE.Vector3())];
  const r = plateDiameter * 0.52;
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    samples.push(depthAt(u.clone().multiplyScalar(Math.cos(a) * r).addScaledVector(v, Math.sin(a) * r)));
  }
  const valid = samples.filter((d): d is number => d !== null);
  const depth = (valid.length ? Math.max(...valid) : Math.abs(size.dot(normal)) / 2) + plateDiameter * 0.012;

  return { center: faceCenter, normal, depth, plateDiameter, dots };
}

function centroid(pts: THREE.Vector3[]): THREE.Vector3 {
  const c = new THREE.Vector3();
  pts.forEach((p) => c.add(p));
  return c.divideScalar(pts.length);
}
